(function () {
  const STORAGE_PRODUCTS = 'hakeem_quote_products';
  const SEARCH_DEBOUNCE_MS = 300;
  const SYNC_EVENT = 'hakeem-quote-basket-changed';

  function loadJson(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function saveJson(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* ignore quota */
    }
  }

  function parseSuggestListMajor(priceRaw, priceMin, priceMax) {
    const src = priceRaw ?? priceMin ?? priceMax;
    if (src == null || src === '') return null;
    const str = String(src).trim();
    if (!str.length) return null;
    const n =
      typeof src === 'number'
        ? Number(src)
        : parseFloat(str.replace(/^[^\d.-]+/, ''));
    if (!Number.isFinite(n)) return null;
    if (Number.isInteger(n) && n >= 75000) return n / 100;
    if (Number.isInteger(n) && n >= 7500 && str.indexOf('.') === -1)
      return n / 100;
    return n;
  }

  function parseSuggestProducts(raw) {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const list =
        parsed?.resources?.results?.products ||
        parsed?.resources?.results?.product ||
        [];
      if (!Array.isArray(list)) return [];
      return list.map((p, index) => {
        const id = String(p.id ?? p.handle ?? p.title + (p.url || '') + index);
        const img =
          p.featured_image ||
          p.image ||
          (p.featured_media && p.featured_media.preview_image) ||
          null;
        const imageUrl =
          typeof img === 'string' ? img : img?.url || img?.src || '';
        const listedMajor = parseSuggestListMajor(
          p.price,
          p.price_min,
          p.price_max
        );
        const priceFallback =
          p.price_min != null
            ? String(p.price_min)
            : p.price != null
              ? String(p.price)
              : '';
        return {
          id,
          title: p.title || '',
          url: p.url || '#',
          image: imageUrl,
          price: priceFallback,
          listedMajor,
        };
      });
    } catch (e) {
      return [];
    }
  }

  /** Benehal Black/White listings that differ only by pack size. */
  const BENEHAL_PACK_TITLE_PATTERN =
    /^Benehal\s+(Black|White)\s+Pack\s+of\s+\d+/i;
  const PACK_OF_QUANTITY_SEGMENT = /\s+Pack\s+of\s+\d+/gi;

  function isBenehalQuantityVariant(title) {
    return BENEHAL_PACK_TITLE_PATTERN.test(String(title || '').trim());
  }

  function getQuoteProductDisplayTitle(title) {
    const trimmed = String(title || '').trim();
    if (isBenehalQuantityVariant(trimmed)) {
      return trimmed.replace(PACK_OF_QUANTITY_SEGMENT, '');
    }
    return trimmed;
  }

  function getQuoteProductGroupKey(product) {
    const title = String(product?.title || '').trim();
    if (isBenehalQuantityVariant(title)) {
      return getQuoteProductDisplayTitle(title).toLowerCase();
    }
    return String(product?.id ?? title);
  }

  /**
   * One search row per Benehal color; other products stay separate.
   * @param {ReturnType<typeof parseSuggestProducts>} products
   */
  function dedupeQuoteSearchProducts(products) {
    const seen = new Set();
    const unique = [];
    for (const product of products) {
      const key = getQuoteProductGroupKey(product);
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push({
        ...product,
        displayTitle: getQuoteProductDisplayTitle(product.title),
      });
    }
    return unique;
  }

  function getStorefrontRoot() {
    if (
      typeof Shopify !== 'undefined' &&
      Shopify.routes &&
      typeof Shopify.routes.root === 'string'
    ) {
      return Shopify.routes.root.replace(/\/?$/, '/');
    }
    return '/';
  }

  /**
   * @param {unknown[]} products
   * @returns {ReturnType<typeof parseSuggestProducts>}
   */
  function parseCollectionProducts(products) {
    if (!Array.isArray(products)) return [];
    return products.map((p, index) => {
      const variant =
        Array.isArray(p.variants) && p.variants.length ? p.variants[0] : null;
      const listedMajor = parseSuggestListMajor(
        variant?.price,
        p.price_min,
        p.price_max
      );
      const priceFallback =
        listedMajor != null
          ? String(listedMajor)
          : variant?.price != null
            ? String(variant.price)
            : '';
      const imageCandidate =
        p.featured_image ||
        (Array.isArray(p.images) && p.images.length
          ? typeof p.images[0] === 'string'
            ? p.images[0]
            : p.images[0]?.src
          : '');
      const handle = p.handle ? String(p.handle) : '';
      return {
        id: String(p.id ?? handle ?? p.title + index),
        title: p.title || '',
        url: p.url || (handle ? `/products/${handle}` : '#'),
        image: imageCandidate || '',
        price: priceFallback,
        listedMajor,
      };
    });
  }

  /**
   * @param {ReturnType<typeof dedupeQuoteSearchProducts>} products
   * @param {string} query
   */
  function filterCatalogProducts(products, query) {
    const q = String(query || '')
      .trim()
      .toLowerCase();
    if (!q.length) return products;
    return products.filter((p) => {
      const haystack = `${p.displayTitle || ''} ${p.title || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }

  function normalizeBasketLine(raw) {
    const qtyParsed = parseInt(
      String(raw.quantity != null ? raw.quantity : ''),
      10
    );
    const qty = Number.isFinite(qtyParsed) && qtyParsed >= 1 ? qtyParsed : 1;
    let listedMajor =
      raw.listedMajor !== undefined && raw.listedMajor !== null
        ? Number(raw.listedMajor)
        : null;
    if (listedMajor != null && !Number.isFinite(listedMajor)) listedMajor = null;
    if (listedMajor == null && raw.price != null && String(raw.price).trim()) {
      const inferred = parseFloat(String(raw.price).replace(/[^\d.-]+/g, ''));
      if (Number.isFinite(inferred)) listedMajor = inferred;
    }
    const offerRaw = raw.offerPerPiece;
    const offerPerPiece =
      offerRaw === undefined || offerRaw === null ? '' : String(offerRaw).trim();

    const rawTitle = String(raw.title ?? '');
    return {
      id: String(raw.id ?? ''),
      title: getQuoteProductDisplayTitle(rawTitle),
      url: String(raw.url ?? '#'),
      image: String(raw.image ?? ''),
      price: String(raw.price ?? ''),
      listedMajor,
      quantity: qty,
      offerPerPiece,
    };
  }

  function escapeHtml(text) {
    const d = document.createElement('div');
    d.textContent = text ?? '';
    return d.innerHTML;
  }

  function escapeAttr(text) {
    return escapeHtml(text).replace(/"/g, '&quot;');
  }

  function formatQuoteProductsPayload(basket, formatMoneyMajor) {
    const lines = [];
    for (const ln of basket) {
      const qty = Math.max(1, ln.quantity || 1);
      let listed =
        ln.listedMajor != null && Number.isFinite(Number(ln.listedMajor))
          ? formatMoneyMajor(Number(ln.listedMajor))
          : String(ln.price || '').trim() || '';

      let offerShown =
        ln.offerPerPiece != null ? String(ln.offerPerPiece).trim() : '';
      if (offerShown) {
        const nParsed = Number.parseFloat(
          String(offerShown).replace(/[^\d.-]+/g, '')
        );
        if (Number.isFinite(nParsed))
          offerShown = `${formatMoneyMajor(nParsed)} / pc (offer)`;
      }

      lines.push(
        `- ${ln.title || '(product)'} | qty ${qty} | listed: ${listed}` +
          (offerShown ? ` | ${offerShown}` : '') +
          (ln.url && ln.url !== '#' ? `\n  ${ln.url}` : '')
      );
    }
    return lines.join('\n');
  }

  class HakeemQuoteBasketPanel {
    /**
     * @param {HTMLElement} host
     */
    constructor(host) {
      this.host = host;
      this.selectedEmpty = host.querySelector('[data-quote-selected-empty]');
      this.selectedList = host.querySelector('[data-quote-selected-list]');
      this.searchPanel = host.querySelector('[data-quote-search-panel]');
      this.searchInput = host.querySelector('[data-quote-search-input]');
      this.searchResults = host.querySelector('[data-quote-search-results]');

      this.basket = loadJson(STORAGE_PRODUCTS, []).map((row) =>
        normalizeBasketLine(row)
      );
      this.searchSelection = [];
      this.searchDebounce = null;
      this.abortSearch = null;
      this.searchOverlayOpen = false;
      this._suppressBasketSync = false;
      this.catalogProducts = null;
      this.catalogLoadPromise = null;

      this.bindEvents();
      this.renderBasket();
      document.addEventListener(SYNC_EVENT, () => this.syncFromStorage());
    }

    bindEvents() {
      this.host
        .querySelector('[data-quote-add-product]')
        ?.addEventListener('click', () => this.openSearch());

      this.selectedList?.addEventListener('click', (e) =>
        this.handleBasketClick(e)
      );
      this.selectedList?.addEventListener('change', (e) =>
        this.handleBasketChange(e)
      );

      this.searchPanel
        ?.querySelectorAll('[data-quote-search-close]')
        .forEach((btn) =>
          btn.addEventListener('click', () => this.closeSearch(true))
        );

      this.searchPanel
        ?.querySelector('[data-quote-search-confirm]')
        ?.addEventListener('click', () => this.confirmSearchSelection());

      this.searchInput?.addEventListener('input', () =>
        this.scheduleSearch()
      );
    }

    syncFromStorage() {
      if (this._suppressBasketSync) return;
      this._suppressBasketSync = true;
      this.basket = loadJson(STORAGE_PRODUCTS, []).map((row) =>
        normalizeBasketLine(row)
      );
      this.renderBasket();
      this._suppressBasketSync = false;
    }

    notifyBasketChanged() {
      if (this._suppressBasketSync) return;
      document.dispatchEvent(new CustomEvent(SYNC_EVENT));
    }

    formatMoneyMajor(majorAmount) {
      if (majorAmount == null || !Number.isFinite(majorAmount)) return '';
      const code = this.host.dataset.currencyCode || 'USD';
      try {
        const lang = document.documentElement.lang || undefined;
        return new Intl.NumberFormat(lang, {
          style: 'currency',
          currency: code,
        }).format(majorAmount);
      } catch (eFmt) {
        const sym = this.host.dataset.currencySymbol || '$';
        return `${sym}${majorAmount.toFixed(2)}`;
      }
    }

    formatSuggestedPrice(product) {
      if (
        product.listedMajor != null &&
        Number.isFinite(product.listedMajor)
      ) {
        return this.formatMoneyMajor(product.listedMajor);
      }
      const s = String(product.price || '').trim();
      const sym = this.host.dataset.currencySymbol || '$';
      if (s === '') return '';
      if (!s.includes('$') && /^[\d.]/.test(s)) return `${sym}${s}`;
      return s;
    }

    decodeBasketLineId(encoded) {
      if (!encoded) return '';
      try {
        return decodeURIComponent(encoded);
      } catch (_) {
        return encoded;
      }
    }

    updateLineQuantity(lineId, nextQtyRaw) {
      const i = this.basket.findIndex((p) => p.id === lineId);
      if (i === -1) return;
      const parsed = Number(nextQtyRaw);
      const qBase = Number.isFinite(parsed) ? Math.floor(parsed) : 1;
      const q = Math.max(1, Math.min(999999, qBase || 1));
      if (this.basket[i].quantity === q) return;
      this.basket[i] = Object.assign({}, this.basket[i], { quantity: q });
      saveJson(STORAGE_PRODUCTS, this.basket);
      this.renderBasket();
      this.notifyBasketChanged();
    }

    handleBasketClick(e) {
      const rm = e.target.closest('[data-remove-product]');
      if (rm instanceof HTMLElement) {
        const enc = rm.getAttribute('data-remove-product') || '';
        const id = enc ? this.decodeBasketLineId(enc) : '';
        this.basket = this.basket.filter((p) => p.id !== id);
        saveJson(STORAGE_PRODUCTS, this.basket);
        this.renderBasket();
        this.notifyBasketChanged();
        return;
      }

      const lineWrap = e.target.closest('[data-basket-line]');
      const encoded = lineWrap?.getAttribute('data-basket-line') || '';
      const lineId = encoded ? this.decodeBasketLineId(encoded) : '';

      const plusBtn = e.target.closest('[data-basket-qty-plus]');
      if (plusBtn && lineId) {
        const existing = this.basket.find((p) => p.id === lineId);
        if (!existing) return;
        this.updateLineQuantity(lineId, (existing.quantity || 1) + 1);
        return;
      }

      const minusBtn = e.target.closest('[data-basket-qty-minus]');
      if (minusBtn && lineId) {
        const existing = this.basket.find((p) => p.id === lineId);
        if (!existing) return;
        this.updateLineQuantity(
          lineId,
          Math.max(1, (existing.quantity || 1) - 1)
        );
      }
    }

    handleBasketChange(ev) {
      const t = ev.target;
      if (!(t instanceof HTMLInputElement)) return;

      const lineWrap = t.closest('[data-basket-line]');
      const encoded = lineWrap?.getAttribute('data-basket-line') || '';
      const lineId = encoded ? this.decodeBasketLineId(encoded) : '';
      if (!lineId) return;

      if (t.matches('[data-basket-qty-input]')) {
        this.updateLineQuantity(lineId, t.value);
        return;
      }

      if (t.matches('[data-basket-offer-input]')) {
        const i = this.basket.findIndex((p) => p.id === lineId);
        if (i === -1) return;
        this.basket[i] = Object.assign({}, this.basket[i], {
          offerPerPiece: t.value.trim(),
        });
        saveJson(STORAGE_PRODUCTS, this.basket);
        this.notifyBasketChanged();
      }
    }

    renderBasket() {
      if (!this.selectedEmpty || !this.selectedList) return;

      if (this.basket.length === 0) {
        this.selectedEmpty.hidden = false;
        this.selectedList.classList.add('visually-hidden');
        this.selectedList.innerHTML = '';
        return;
      }

      const listedLbl = this.host.dataset.labelListed || 'Listed price';
      const qtyLbl = this.host.dataset.labelQty || 'Quantity';
      const offerLbl =
        this.host.dataset.labelOffer || 'Your offer ($/piece)';
      const currencySymEsc = escapeHtml(
        this.host.dataset.currencySymbol || '$'
      );

      this.selectedEmpty.hidden = true;
      this.selectedList.classList.remove('visually-hidden');

      const linesHtml = this.basket.map((item) => {
        const lineIdEsc = escapeAttr(
          encodeURIComponent(String(item.id ?? ''))
        );
        let listedParagraph = '';
        if (
          item.listedMajor != null &&
          Number.isFinite(Number(item.listedMajor))
        ) {
          const disp = escapeHtml(this.formatMoneyMajor(item.listedMajor));
          listedParagraph = `<p class="quote-modal__listed"><span class="quote-modal__listed-label">${escapeHtml(listedLbl)}: </span><strong>${disp}</strong><span class="quote-modal__listed-unit"> / piece</span></p>`;
        } else {
          const disp = escapeHtml(this.formatSuggestedPrice(item));
          listedParagraph = disp
            ? `<p class="quote-modal__listed"><span class="quote-modal__listed-label">${escapeHtml(listedLbl)}: </span><strong>${disp}</strong><span class="quote-modal__listed-unit"> / piece</span></p>`
            : '';
        }

        const qtyVal = Math.max(1, item.quantity || 1);
        const offerValEsc = escapeAttr(String(item.offerPerPiece ?? '').trim());

        return `
      <article class="quote-modal__selected-item" data-basket-line="${lineIdEsc}">
        ${item.image ? `<img class="quote-modal__selected-thumb" alt="" loading="lazy" src="${escapeAttr(item.image)}"/>` : '<div class="quote-modal__selected-thumb"></div>'}
        <div class="quote-modal__selected-meta">
          <strong class="quote-modal__selected-title">${escapeHtml(item.title)}</strong>
          ${listedParagraph}
          <div class="quote-modal__line-controls" role="group" aria-label="${escapeHtml(qtyLbl + ', ' + offerLbl)}">
            <div class="quote-modal__control-block">
              <span class="quote-modal__control-label">${escapeHtml(qtyLbl)}</span>
              <div class="quote-modal__stepper">
                <button type="button" class="quote-modal__stepper-btn" data-basket-qty-minus aria-label="Decrease quantity">&minus;</button>
                <input class="quote-modal__qty-input" type="number" min="1" max="999999" step="1" inputmode="numeric" value="${qtyVal}" data-basket-qty-input aria-label="${escapeHtml(qtyLbl)}" />
                <button type="button" class="quote-modal__stepper-btn" data-basket-qty-plus aria-label="Increase quantity">+</button>
              </div>
            </div>
            <div class="quote-modal__control-block quote-modal__control-block--grow">
              <span class="quote-modal__control-label">${escapeHtml(offerLbl)}</span>
              <div class="quote-modal__offer-field">
                <span class="quote-modal__offer-prefix" aria-hidden="true">${currencySymEsc}</span>
                <input class="quote-modal__offer-input" type="number" inputmode="decimal" min="0" step="0.01" placeholder="0.00" value="${offerValEsc}" data-basket-offer-input aria-label="${escapeHtml(offerLbl)}" />
              </div>
            </div>
          </div>
        </div>
        <button type="button" class="quote-modal__selected-remove" data-remove-product="${lineIdEsc}">
          Remove
        </button>
      </article>`;
      });

      this.selectedList.innerHTML = linesHtml.join('');
    }

    openSearch() {
      if (!this.searchPanel) return;
      this.searchSelection = [];
      this.searchOverlayOpen = true;
      this.searchPanel.hidden = false;
      this.searchPanel.setAttribute('aria-hidden', 'false');
      if (this.searchInput) this.searchInput.value = '';
      if (this.searchResults) {
        this.searchResults.innerHTML =
          '<p class="quote-modal__search-hint">Type to search products…</p>';
      }
      setTimeout(() => this.searchInput?.focus(), 50);
      this.scheduleSearch(true);
    }

    closeSearch() {
      if (!this.searchPanel) return;
      this.searchOverlayOpen = false;
      this.searchPanel.hidden = true;
      this.searchPanel.setAttribute('aria-hidden', 'true');
      if (this.abortSearch) this.abortSearch.abort();
    }

    scheduleSearch(forceInitial = false) {
      window.clearTimeout(this.searchDebounce);
      this.searchDebounce = window.setTimeout(
        () => this.runProductSearch(forceInitial),
        forceInitial ? 0 : SEARCH_DEBOUNCE_MS
      );
    }

    buildSuggestProductUrl(searchQuery) {
      const qs = [
        `q=${encodeURIComponent(searchQuery)}`,
        `resources[type]=product`,
        `resources[limit]=10`,
        `resources[limit_scope]=all`,
      ].join('&');

      const root =
        typeof Shopify !== 'undefined' &&
        Shopify.routes &&
        typeof Shopify.routes.root === 'string'
          ? Shopify.routes.root
          : null;

      if (root) {
        const base = root.replace(/\/?$/, '/');
        return `${base}search/suggest.json?${qs}`;
      }

      const baseSuggest =
        (window.routes &&
          typeof window.routes.predictive_search_url === 'string' &&
          window.routes.predictive_search_url) ||
        '/search/suggest';
      let pathname = baseSuggest.split('?')[0].replace(/\.json$/i, '');
      if (!pathname.endsWith('/suggest')) {
        pathname = pathname.replace(/\/?search\/?$/i, '/search') + '/suggest';
      }
      pathname = `${pathname}.json`;
      if (!pathname.startsWith('/')) pathname = `/${pathname}`;
      try {
        return new URL(`${pathname}?${qs}`, window.location.href).href;
      } catch (e2) {
        return `${pathname}?${qs}`;
      }
    }

    async loadCatalogProducts() {
      if (this.catalogProducts) return this.catalogProducts;
      if (this.catalogLoadPromise) return this.catalogLoadPromise;

      this.catalogLoadPromise = (async () => {
        const root = getStorefrontRoot();
        const collected = [];
        let page = 1;

        while (page <= 20) {
          const url = `${root}collections/all/products.json?limit=250&page=${page}`;
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(String(res.status));
          const data = await res.json();
          const batch = Array.isArray(data.products) ? data.products : [];
          collected.push(...batch);
          if (batch.length < 250) break;
          page += 1;
        }

        const catalog = dedupeQuoteSearchProducts(
          parseCollectionProducts(collected)
        );
        this.catalogProducts = catalog;
        return catalog;
      })();

      try {
        return await this.catalogLoadPromise;
      } finally {
        this.catalogLoadPromise = null;
      }
    }

    async runProductSearch() {
      const q = (this.searchInput?.value || '').trim();

      if (!this.searchResults) return;

      if (this.abortSearch) this.abortSearch.abort();
      this.abortSearch = new AbortController();
      const { signal } = this.abortSearch;

      this.searchResults.innerHTML =
        '<p class="quote-modal__search-loading">Loading…</p>';

      try {
        const catalog = await this.loadCatalogProducts();
        if (signal.aborted) return;
        const products = filterCatalogProducts(catalog, q);
        this.renderSearchResults(products);
      } catch (e) {
        if (e && e.name === 'AbortError') return;

        try {
          const fallbackQuery = q.length ? q : 'n95';
          const url = this.buildSuggestProductUrl(fallbackQuery);
          const res = await fetch(url, {
            signal,
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error(String(res.status));
          const data = await res.json();
          if (signal.aborted) return;
          const products = dedupeQuoteSearchProducts(parseSuggestProducts(data));
          this.renderSearchResults(products);
        } catch (fallbackErr) {
          if (fallbackErr && fallbackErr.name === 'AbortError') return;
          this.searchResults.innerHTML =
            '<p class="quote-modal__search-error">Could not load products. Try again.</p>';
        }
      }
    }

    renderSearchResults(products) {
      if (!this.searchResults) return;
      if (!products.length) {
        this.searchResults.innerHTML =
          '<p class="quote-modal__search-hint">No products found.</p>';
        return;
      }

      this.searchResults.innerHTML = products
        .map((p) => {
          const checked = this.searchSelection.some((s) => s.id === p.id);
          return `
        <label class="quote-modal__search-row">
          <input type="checkbox" data-search-pick value="${escapeAttr(encodeURIComponent(p.id))}" ${checked ? 'checked' : ''}/>
          ${p.image ? `<img src="${escapeAttr(p.image)}" alt=""/>` : '<div></div>'}
          <div>
            <div class="quote-modal__search-row-title">${escapeHtml(p.displayTitle || p.title)}</div>
            <div class="quote-modal__search-row-price">${escapeHtml(this.formatSuggestedPrice(p))}</div>
          </div>
        </label>`;
        })
        .join('');

      this.searchResults.querySelectorAll('[data-search-pick]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.addEventListener('change', () => {
          let rawId = input.value;
          try {
            rawId = decodeURIComponent(rawId);
          } catch (e2) {
            /* keep raw */
          }
          const product = products.find((x) => x.id === rawId);
          if (!product) return;
          if (input.checked) {
            if (!this.searchSelection.some((s) => s.id === product.id))
              this.searchSelection.push(product);
          } else {
            this.searchSelection = this.searchSelection.filter(
              (s) => s.id !== product.id
            );
          }
        });
      });
    }

    confirmSearchSelection() {
      if (this.searchSelection.length) {
        const existingIds = new Set(this.basket.map((x) => x.id));
        const existingGroupKeys = new Set(
          this.basket.map((x) => getQuoteProductGroupKey(x))
        );
        this.searchSelection.forEach((p) => {
          const groupKey = getQuoteProductGroupKey(p);
          if (existingGroupKeys.has(groupKey) || existingIds.has(p.id)) return;
          existingGroupKeys.add(groupKey);
          existingIds.add(p.id);
          this.basket.push(
            normalizeBasketLine({
              id: p.id,
              title: p.displayTitle || p.title,
              url: p.url,
              image: p.image,
              price: p.price,
              listedMajor: p.listedMajor,
              quantity: 1,
              offerPerPiece: '',
            })
          );
        });
        saveJson(STORAGE_PRODUCTS, this.basket);
        this.renderBasket();
        this.notifyBasketChanged();
      }
      this.closeSearch();
    }

    getBasket() {
      return this.basket;
    }

    formatProductsForContact() {
      return formatQuoteProductsPayload(this.basket, (n) =>
        this.formatMoneyMajor(n)
      );
    }
  }

  window.HakeemQuoteBasketPanel = HakeemQuoteBasketPanel;
})();
